import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import LiveActivity from "@/components/LiveActivity";
import TokenIcon from "@/components/TokenIcon";
import PageTransition from "@/components/PageTransition";
import BoostCarousel from "@/components/BoostCarousel";
import TrustpilotBadge from "@/components/TrustpilotBadge";
import FAQ from "@/components/FAQ";
import CountUp from "@/components/CountUp";
import { EVM_TOKENS, SOL_TOKENS } from "@/data/tokens";
import { PARTNERS, TESTIMONIALS } from "@/data/social";
import { ArrowRight, Zap, ShieldCheck, Layers, TrendingUp, Quote } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { fmtNum } from "@/lib/wallet";

export default function Landing() {
  const totalTvl = [...EVM_TOKENS, ...SOL_TOKENS].reduce((a, b) => a + b.tvl, 0);
  const tokenCount = EVM_TOKENS.length + SOL_TOKENS.length;
  const avgApr = ([...EVM_TOKENS, ...SOL_TOKENS].reduce((a, b) => a + b.apr, 0) / tokenCount).toFixed(1);
  const topBoost = Math.max(...[...EVM_TOKENS, ...SOL_TOKENS].map(t => t.boostApr || 0)).toFixed(0);

  // Scroll to hash target (#faq, etc.) when navigated from another page.
  const loc = useLocation();
  useEffect(() => {
    if (!loc.hash) return;
    const id = loc.hash.replace("#", "");
    const tries = [80, 280, 600];
    tries.forEach((d) => setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, d));
  }, [loc.hash]);

  return (
    <PageTransition>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 mesh-hero" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 sm:pt-20 pb-12 sm:pb-16">
          <div className="text-[11px] uppercase tracking-[0.3em] mb-5 sm:mb-6" style={{ color: "var(--text-3)" }}>
            ▍ INSTITUTIONAL VALIDATOR NETWORK · v2.4
          </div>
          <h1 className="font-display font-black leading-[0.95] tracking-tighter text-4xl sm:text-6xl lg:text-7xl max-w-5xl">
            Stake any asset.<br />
            <span className="grad-sol-text">Earn validator-grade yield.</span>
          </h1>
          <p className="mt-5 sm:mt-6 max-w-2xl text-sm sm:text-base lg:text-lg" style={{ color: "var(--text-2)" }}>
            Stakeforge aggregates 110+ liquid assets across EVM and Solana, plugged directly into our
            validator partner mesh — every reward auto-compounds back into your position.
          </p>

          <div className="flex flex-wrap gap-3 mt-8 sm:mt-10">
            <Link
              to="/markets/evm"
              data-testid="cta-evm-link"
              className="group inline-flex items-center gap-3 px-5 sm:px-6 h-11 sm:h-12 text-sm sm:text-base font-bold tracking-wide grad-evm-bg btn-glow-evm"
              style={{ color: "#fff" }}
            >
              EVM NETWORK <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link
              to="/markets/solana"
              data-testid="cta-solana-link"
              className="group inline-flex items-center gap-3 px-5 sm:px-6 h-11 sm:h-12 text-sm sm:text-base font-bold tracking-wide grad-sol-bg btn-glow-sol"
              style={{ color: "#fff" }}
            >
              SOLANA NETWORK <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>

        {/* Metrics strip */}
        <div className="border-t border-b" style={{ borderColor: "var(--border)" }}>
          <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4">
            <Metric
              label="Total Value Locked"
              valueNode={<CountUp end={totalTvl} duration={2000} format={(v) => "$" + fmtNum(v, 2)} />}
              hint="across all pools"
            />
            <Metric
              label="Assets Supported"
              valueNode={<CountUp end={tokenCount} duration={1400} />}
              hint="EVM + Solana"
            />
            <Metric
              label="Avg Boosted APR"
              valueNode={<CountUp end={parseFloat(avgApr)} decimals={1} duration={1600} suffix="%" />}
              hint="standard pools"
            />
            <Metric
              label="Top 24h Boost"
              valueNode={<CountUp end={parseFloat(topBoost)} decimals={0} duration={1800} suffix="%" />}
              hint="daily auction pool"
              boost
            />
          </div>
        </div>
      </section>

      {/* Boost carousel */}
      <BoostCarousel />

      {/* Trustpilot — independent customer rating */}
      <TrustpilotBadge />

      {/* Partners marquee */}
      <Partners />

      {/* Live activity */}
      <LiveActivity />

      {/* Why boosted */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-3 gap-8 items-start">
          <div className="lg:col-span-1">
            <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>▍ Why boosted yield</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter leading-tight">
              We don't print APR.<br />
              We split <span className="grad-evm-text">validator MEV</span>.
            </h2>
            <p className="mt-5 text-sm" style={{ color: "var(--text-2)" }}>
              Stakeforge maintains exclusive revenue-share agreements with 34 enterprise validators.
              Their MEV + priority-fee surplus flows into a smoothing pool which auto-compounds into
              your staked position every 12 hours.
            </p>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-px" style={{ background: "var(--border)" }}>
            <Feature icon={<Layers size={18} />}      title="Validator Partner Mesh" text="34 institutional validators routing block-space revenue back into the pool." />
            <Feature icon={<TrendingUp size={18} />}  title="12h Auto-Compounding"   text="Rewards reinvest continuously — your APR is the effective APY." />
            <Feature icon={<Zap size={18} />}         title="MEV Smoothing Pool"     text="Priority-fee and MEV surplus shared across all delegators, smoothing variance." />
            <Feature icon={<ShieldCheck size={18} />} title="Slashing Insurance"     text="2.5% of pool revenue funds an on-chain insurance vault. Slashing covered up to 100%." />
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <Testimonials />

      {/* FAQ */}
      <FAQ />

      {/* Chain split */}
      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="text-[11px] uppercase tracking-[0.3em] mb-6" style={{ color: "var(--text-3)" }}>▍ Pick a network</div>
        <div className="grid md:grid-cols-2 gap-6">
          <ChainCard
            to="/markets/evm" label="EVM Network" count={EVM_TOKENS.length}
            tvl={EVM_TOKENS.reduce((a, b) => a + b.tvl, 0)}
            accent="var(--evm)" grad="grad-evm-bg" testid="chain-card-evm"
            description="Ethereum, BNB Chain, Polygon, Arbitrum, Optimism."
            top={EVM_TOKENS.slice(0, 6)}
          />
          <ChainCard
            to="/markets/solana" label="Solana" count={SOL_TOKENS.length}
            tvl={SOL_TOKENS.reduce((a, b) => a + b.tvl, 0)}
            accent="var(--sol)" grad="grad-sol-bg" testid="chain-card-solana"
            description="High-throughput L1. Lowest unstake latency on the platform."
            top={SOL_TOKENS.slice(0, 6)}
          />
        </div>
      </section>

      <Footer />
    </PageTransition>
  );
}

function Metric({ label, value, valueNode, hint, boost }) {
  return (
    <div className="px-2 sm:px-6 py-6 border-r last:border-r-0" style={{ borderColor: "var(--border)" }}>
      <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{label}</div>
      <div className={`font-mono font-bold text-3xl sm:text-4xl mt-2 tracking-tight tabular-nums ${boost ? "grad-boost-text" : ""}`}>{valueNode || value}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-2)" }}>{hint}</div>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="p-6" style={{ background: "var(--surface-1)" }}>
      <div className="w-9 h-9 grid place-items-center border mb-4" style={{ borderColor: "var(--border-strong)" }}>{icon}</div>
      <div className="font-display font-extrabold tracking-tight">{title}</div>
      <div className="text-sm mt-2" style={{ color: "var(--text-2)" }}>{text}</div>
    </div>
  );
}

function ChainCard({ to, label, count, tvl, accent, grad, description, top, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="block border lift p-8 relative overflow-hidden group"
      style={{ background: "var(--surface-1)", borderColor: "var(--border)" }}
    >
      <div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
        style={{
          background: accent === "var(--evm)"
            ? "linear-gradient(135deg, rgba(0,85,255,0.10), rgba(0,212,255,0.05) 50%, transparent 100%)"
            : "linear-gradient(135deg, rgba(153,69,255,0.10), rgba(20,241,149,0.06) 50%, transparent 100%)",
        }}
      />
      <div className="relative flex items-start justify-between mb-8">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em]" style={{ color: accent }}>▍ Network</div>
          <div className="font-display font-black text-4xl tracking-tighter mt-1">{label}</div>
          <div className="text-sm mt-3" style={{ color: "var(--text-2)" }}>{description}</div>
        </div>
        <ArrowRight size={20} style={{ color: accent }} className="group-hover:translate-x-1 transition-transform" />
      </div>

      <div className="relative grid grid-cols-3 gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>Assets</div>
          <div className="font-mono text-2xl font-bold mt-1">{count}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>TVL</div>
          <div className="font-mono text-2xl font-bold mt-1">${fmtNum(tvl, 2)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>Top APR</div>
          <div className={`font-mono text-2xl font-bold mt-1 ${grad === "grad-evm-bg" ? "grad-evm-text" : "grad-sol-text"}`}>
            {Math.max(...top.map(t => t.apr)).toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="relative flex flex-wrap gap-2">
        {top.map(t => (
          <div key={t.symbol} className="flex items-center gap-2 px-2 py-1 border text-xs" style={{ borderColor: "var(--border)" }}>
            <TokenIcon symbol={t.symbol} color={t.color} size="sm" />
            <span className="font-mono">{t.symbol}</span>
          </div>
        ))}
      </div>
    </Link>
  );
}

function Partners() {
  return (
    <section className="border-y" style={{ borderColor: "var(--border)", background: "var(--surface-1)" }}>
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-end justify-between mb-8 flex-wrap gap-2">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em]" style={{ color: "var(--text-3)" }}>▍ Trusted by</div>
            <h2 className="font-display font-extrabold text-2xl sm:text-3xl tracking-tighter mt-2">Validator &amp; ecosystem partners</h2>
          </div>
          <div className="text-xs font-mono" style={{ color: "var(--text-3)" }}>34 validators · 12 ecosystem grants</div>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-px" style={{ background: "var(--border)" }} data-testid="partners-grid">
          {PARTNERS.map((p, i) => (
            <div
              key={p.name}
              data-testid={`partner-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="px-4 py-6 flex flex-col items-center justify-center gap-3 lift fade-up stagger"
              style={{ background: "var(--surface-1)", "--i": i }}
            >
              <PartnerLogo p={p} />
              <span className="text-[11px] uppercase tracking-widest font-mono" style={{ color: "var(--text-2)" }}>{p.name}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function PartnerLogo({ p }) {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className="w-10 h-10 grid place-items-center font-display font-black text-sm border" style={{ borderColor: "var(--border-strong)", color: "var(--text-2)" }}>
        {p.name[0]}
      </div>
    );
  }
  return (
    <img
      src={p.url} alt={p.name} width={40} height={40} loading="lazy" onError={() => setErr(true)}
      style={{ width: 40, height: 40, borderRadius: "999px", objectFit: "cover", background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.05)", filter: "grayscale(0.15)" }}
    />
  );
}

function Testimonials() {
  return (
    <section className="border-t" style={{ borderColor: "var(--border)" }}>
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid lg:grid-cols-3 gap-12 items-start">
          <div className="lg:col-span-1">
            <div className="text-[11px] uppercase tracking-[0.3em] mb-4" style={{ color: "var(--text-3)" }}>▍ Operator notes</div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tighter leading-tight">
              What people stake here.<br />
              <span className="grad-sol-text">And why.</span>
            </h2>
            <p className="text-sm mt-5" style={{ color: "var(--text-2)" }}>
              Quotes pulled with permission from our private operators channel. Identities verified, surnames redacted.
            </p>
          </div>

          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-px" style={{ background: "var(--border)" }} data-testid="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <article
                key={i}
                data-testid={`testimonial-${i}`}
                className="p-6 flex flex-col gap-4 lift fade-up stagger"
                style={{ background: "var(--surface-1)", "--i": i }}
              >
                <Quote size={18} style={{ color: t.avatarColor }} />
                <p className="text-sm leading-relaxed" style={{ color: "var(--text-2)" }}>"{t.quote}"</p>
                <div className="flex items-center gap-3 mt-auto pt-4 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="w-9 h-9 grid place-items-center font-display font-black text-sm" style={{ background: t.avatarColor, color: "#000" }}>{t.name[0]}</div>
                  <div>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-[10px] uppercase tracking-widest" style={{ color: "var(--text-3)" }}>{t.role}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t" style={{ borderColor: "var(--border)" }}>
      <div className="max-w-7xl mx-auto px-6 py-8 grid md:grid-cols-3 gap-4 text-xs items-center" style={{ color: "var(--text-3)" }}>
        <span>© 2026 Stakeforge Labs SA · Zug, Switzerland</span>
        <div className="flex gap-4 md:justify-center">
          <Link to="/legal" data-testid="footer-legal-link" className="hover:text-white">Legal</Link>
          <Link to="/referrals" className="hover:text-white">Refer</Link>
          <Link to="/leaderboard" className="hover:text-white">Leaderboard</Link>
        </div>
        <span className="font-mono md:text-right">v2.4.1 — block #21,408,229</span>
      </div>
    </footer>
  );
}
